import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import {
  CityNameInput,
  CountryInput,
  EmailInput,
  EmergencyPhoneNumberInput,
  MobilePhoneNumberInput,
  NameInput,
  PostalCodeInput,
  StreetNameInput,
  StreetNumberAdditionalInput,
  StreetNumberInput,
  SurnameInput,
  TitleInput,
} from '@/scopes/checkout/components/PersonalDetailsForm/Inputs'
import { BirthdateInput } from '@/scopes/checkout/components/PersonalDetailsForm/Inputs/BirthdateInput'
import { getMainBookedCountryFieldNameSelector } from '@/scopes/checkout/components/PersonalDetailsForm/selectors'
import { useParticipantIndices } from '@/scopes/checkout/components/PersonalDetailsForm/UseParticipantIndices'
import { useParticipantsFormStepStore } from '@/scopes/checkout/infrastructure/store/useParticipantsFormStepStore'
import {
  DateFieldRange,
  Field,
  FieldOption,
  ParticipantFormFieldId,
  ParticipantFormFields,
  RoomWithParticipants,
} from '../../domain/types/checkoutPersonalDetails'
import { ParticipantFormType } from '../../domain/types/participantFormType'
import styles from './Form.module.css'

const PARTICIPANT_TYPE_ICONS: Record<ParticipantFormType, 'PersonInCircle' | 'Person' | 'Child' | 'Baby'> = {
  [ParticipantFormType.MainBooker]: 'PersonInCircle',
  [ParticipantFormType.Adult]: 'Person',
  [ParticipantFormType.Child]: 'Child',
  [ParticipantFormType.Baby]: 'Baby',
}

function getParticipantDescription(type: ParticipantFormType, intl: ReturnType<typeof useIntl>) {
  return type === ParticipantFormType.MainBooker
    ? intl.formatMessage({ id: 'checkout_steps_participants_form_field_participants_description_main_booker' })
    : undefined
}

const PARTICIPANT_TITLE_IDS: Record<ParticipantFormType, string> = {
  [ParticipantFormType.MainBooker]: 'checkout_steps_participants_form_main_booker_section_title',
  [ParticipantFormType.Adult]: 'checkout_steps_participants_form_adult_section_title',
  [ParticipantFormType.Child]: 'checkout_steps_participants_form_child_section_title',
  [ParticipantFormType.Baby]: 'checkout_steps_participants_form_baby_section_title',
}

function getParticipantTitle(type: ParticipantFormType, intl: ReturnType<typeof useIntl>, id: string) {
  const titleId = PARTICIPANT_TITLE_IDS[type]
  return titleId ? intl.formatMessage({ id: titleId }, { key: id }) : ''
}

function getParticipantPopUpInformation(type: ParticipantFormType, intl: ReturnType<typeof useIntl>) {
  return type === ParticipantFormType.MainBooker
    ? {
        popupTitle: intl.formatMessageIfExists({ id: 'checkout_steps_participants_form_main_booker_pop_up_section_title' }),
        popupDescription: intl.formatMessageIfExists({ id: 'checkout_steps_participants_form_main_booker_pop_up_section_description' }),
        popupReadMoreLabel: intl.formatMessageIfExists({ id: 'checkout_steps_read_more' }),
      }
    : undefined
}

export function PersonalDetailsForm() {
  const intl = useIntl()
  const state = useParticipantsFormStepStore(state => state)

  const { participantsByRooms } = state
  const hasMultipleRooms = participantsByRooms.length > 1

  const participantIndices = useParticipantIndices(participantsByRooms)

  return (
    <form className={styles.personalDetailsForm}>
      {participantsByRooms?.map((room: RoomWithParticipants) => {
        return (
          <React.Fragment key={room.id}>
            {hasMultipleRooms && room.name && (
              <Typography as="h2" className={styles.roomName}>
                {room.name}
              </Typography>
            )}
            {room.participants.map((participant: ParticipantFormFields) => {
              const { id, type, fields } = participant
              const currentCount = participantIndices.get(id) || 1
              const { popupTitle, popupDescription, popupReadMoreLabel } = getParticipantPopUpInformation(type, intl) || {}
              return (
                <CheckoutSection
                  key={id}
                  title={getParticipantTitle(type, intl, currentCount.toString())}
                  description={getParticipantDescription(type, intl)}
                  glyph={PARTICIPANT_TYPE_ICONS[type]}
                  moreInformationTitle={popupTitle}
                  moreInformationDescription={popupDescription}
                  moreInformationReadMoreLabel={popupReadMoreLabel}
                >
                  <div className={styles.personalDetailsFormFields}>
                    {fields.map(({ id: fieldId, isMandatory, name, validationPattern, options, dateRange }: Field) => {
                      return (
                        <FieldComponent
                          key={fieldId}
                          id={fieldId}
                          name={name}
                          isMandatory={isMandatory}
                          validationPattern={validationPattern}
                          options={options}
                          dateRange={dateRange}
                          participantType={type}
                        />
                      )
                    })}
                  </div>
                </CheckoutSection>
              )
            })}
          </React.Fragment>
        )
      })}
    </form>
  )
}

function FieldComponent({
  isMandatory,
  id,
  name,
  validationPattern,
  options = [],
  dateRange,
  participantType,
}: {
  id: ParticipantFormFieldId
  isMandatory: boolean
  name: string
  validationPattern?: string
  options?: FieldOption[]
  dateRange?: DateFieldRange
  participantType: ParticipantFormType
}) {
  const countryFieldName = useParticipantsFormStepStore(getMainBookedCountryFieldNameSelector)
  const Component: Partial<Record<ParticipantFormFieldId, React.ReactNode>> = {
    Name: (
      <NameInput
        name={name}
        pattern={validationPattern}
        required={isMandatory}
      />
    ),
    Surname: (
      <SurnameInput
        name={name}
        pattern={validationPattern}
        required={isMandatory}
      />
    ),
    Birthdate: (
      <BirthdateInput
        name={name}
        required={isMandatory}
        dateRange={dateRange}
        participantType={participantType}
      />
    ),
    MobilePhoneNumber: (
      <MobilePhoneNumberInput
        name={name}
        required={isMandatory}
      />
    ),
    EmergencyPhoneNumber: (
      <EmergencyPhoneNumberInput
        name={name}
        required={isMandatory}
      />
    ),
    StreetName: (
      <StreetNameInput
        name={name}
        required={isMandatory}
        pattern={validationPattern}
      />
    ),
    StreetNumber: (
      <StreetNumberInput
        name={name}
        required={isMandatory}
        pattern={validationPattern}
      />
    ),
    StreetNumberAdditional: (
      <StreetNumberAdditionalInput name={name} required={isMandatory} pattern={validationPattern} />
    ),
    CityName: (
      <CityNameInput
        name={name}
        pattern={validationPattern}
        required={isMandatory}
      />
    ),
    Email: (
      <EmailInput name={name} pattern={validationPattern} required={isMandatory} />
    ),
    Title: (
      <TitleInput
        name={name}
        required={isMandatory}
        participantType={participantType}
      />
    ),
    Country: (
      <CountryInput
        name={name}
        required={isMandatory}
        items={options}
      />
    ),
    PostalCode: (
      <PostalCodeInput
        name={name}
        required={isMandatory}
        pattern={validationPattern}
        watchCountryFieldName={countryFieldName}
      />
    ),
  }

  return Component[id]
}
